import React, { useMemo, useState } from "react";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CheckCircle2, Sparkles, Cpu, Link2, ExternalLink } from "lucide-react";
import { humanize } from "./proofApi";
import { StatusPill, Pill, ConfidenceChip } from "./chips";
import { ACTION_LABEL } from "./ResolveGapDrawer";

const PRIORITIES = ["BLOCKER", "HIGH", "MEDIUM", "SUGGESTION"];
const STATUSES = [["open", "Open"], ["in_review", "In review"], ["resolved", "Resolved"], ["dismissed", "Dismissed"]];
const FILTER_GROUPS = {
  CONTRADICTED: ["CONTRADICTED_PROPOSITION", "UNRESOLVED_SOURCE_CONFLICT"],
  VERIFICATION: ["REQUIREMENT_NOT_VERIFIED", "AUTHORITY_NOT_VERIFIED"],
  FOUNDATION: ["DOCUMENT_FOUNDATION_REVIEW", "EXHIBIT_STATUS_UNRESOLVED"],
};

export function GapsView({ gaps, G, initialFilter, focusNodeId, onResolve, onDismiss, onReopen, onInspectNode, onClearFocus }) {
  const [status, setStatus] = useState("open");
  const [prio, setPrio] = useState(null);
  const [type, setType] = useState(initialFilter || null);

  const list = useMemo(() => gaps.filter((g) => {
    const s = g.status;
    const okStatus = status === "open" ? ["OPEN", "REOPENED"].includes(s) : status === "in_review" ? s === "IN_REVIEW" : status === "resolved" ? s === "RESOLVED" : s === "DISMISSED";
    const okPrio = !prio || g.priority === prio;
    const types = type ? FILTER_GROUPS[type] || [type] : null;
    const okType = !types || types.includes(g.gapType);
    const okNode = !focusNodeId || g.affectedNodeId === focusNodeId;
    return okStatus && okPrio && okType && okNode && g.reviewState !== "PROPOSED";
  }).sort((a, b) => PRIORITIES.indexOf(a.priority) - PRIORITIES.indexOf(b.priority)), [gaps, status, prio, type, focusNodeId]);

  const types = [...new Set(gaps.map((g) => g.gapType))];
  const activeFilters = [prio, type, focusNodeId].filter(Boolean).length;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs" data-testid="pg-gap-filters">
        <div className="flex rounded-lg border border-line bg-surface p-0.5">{STATUSES.map(([k, l]) => <button key={k} onClick={() => setStatus(k)} className={cn("rounded-md px-2.5 py-1", status === k ? "bg-navy text-white" : "text-ink-2")} data-testid={`pg-gap-status-${k}`}>{l}</button>)}</div>
        <div className="flex gap-1">{PRIORITIES.map((p) => <button key={p} onClick={() => setPrio(prio === p ? null : p)} className={cn("rounded-full border px-2.5 py-1", prio === p ? "border-copper bg-copper-light text-copper" : "border-line text-ink-2")}>{humanize(p)}</button>)}</div>
        <select value={type || ""} onChange={(e) => setType(e.target.value || null)} className="rounded-md border border-line bg-card px-2 py-1 text-xs text-ink-2" data-testid="pg-gap-type-filter">
          <option value="">All gap types</option>
          {Object.keys(FILTER_GROUPS).map((k) => <option key={k} value={k}>{humanize(k)} (group)</option>)}
          {types.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}
        </select>
        {focusNodeId && <Pill tone="copper">Focused on: {G.N[focusNodeId]?.label?.slice(0, 40)} <button onClick={onClearFocus} className="ml-1">×</button></Pill>}
        {activeFilters > 0 && <button onClick={() => { setPrio(null); setType(null); onClearFocus?.(); }} className="text-copper hover:underline">Clear all ({activeFilters})</button>}
        <span className="ml-auto text-ink-muted">Priority = workflow urgency / materiality, not outcome probability.</span>
      </div>

      {list.length === 0 ? (
        <EmptyState icon={CheckCircle2} title={status === "open" ? "No unresolved structural gaps are currently identified." : "Nothing here."} message={status === "open" ? "Continue professional review as the record evolves." : "Adjust the filters to see other gaps."} />
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2" data-testid="pg-gap-list">
          {list.map((g) => <GapCard key={g.id} g={g} G={G} onResolve={onResolve} onDismiss={onDismiss} onReopen={onReopen} onInspectNode={onInspectNode} />)}
        </div>
      )}
    </div>
  );
}

export function GapCard({ g, G, onResolve, onDismiss, onReopen, onInspectNode, compact = false }) {
  const node = g.affectedNodeId ? G?.N?.[g.affectedNodeId] : null;
  const isOpen = ["OPEN", "REOPENED", "IN_REVIEW"].includes(g.status);
  return (
    <div className={cn("rounded-xl border border-line bg-card p-4", g.priority === "BLOCKER" && isOpen && "border-l-4 border-l-danger")} data-testid={`pg-gap-${g.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusPill status={g.priority} />
          <Pill tone="muted">{humanize(g.gapType)}</Pill>
          <StatusPill status={g.status} />
          <Pill tone={g.detectionMethod === "AI" ? "info" : "muted"} title="How this was detected">{g.detectionMethod === "AI" ? <Sparkles className="h-3 w-3" /> : <Cpu className="h-3 w-3" />}{g.detectionMethod === "AI" ? "AI proposed" : "Deterministic"}</Pill>
          <ConfidenceChip value={g.confidence} />
        </div>
      </div>
      <h4 className="mt-2 font-display text-base font-semibold text-ink">{g.title}</h4>
      {node && <button onClick={() => onInspectNode?.(node)} className="mt-0.5 text-xs text-copper hover:underline" data-testid={`pg-gap-node-${g.id}`}>Affects: {node.label}</button>}
      {!compact && (
        <dl className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
          <div><dt className="font-medium text-ink-2">Why flagged</dt><dd className="mt-0.5 text-ink-2">{g.whyFlagged || g.description}</dd></div>
          {g.missing && <div><dt className="font-medium text-ink-2">What is missing</dt><dd className="mt-0.5 text-ink-2">{g.missing}</dd></div>}
          {g.consequence && <div className="sm:col-span-2"><dt className="font-medium text-ink-2">Structural consequence</dt><dd className="mt-0.5 text-ink-2">{g.consequence}</dd></div>}
        </dl>
      )}
      {(g.links || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">{g.links.map((l, i) => <span key={i} className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-ink-2"><Link2 className="h-3 w-3" />{l.label || humanize(l.type)}</span>)}</div>
      )}
      {g.resolutionNote && <div className="mt-2 rounded bg-surface px-2 py-1 text-xs text-ink-2">Note: {g.resolutionNote}</div>}
      {g.dismissReason && <div className="mt-2 rounded bg-surface px-2 py-1 text-xs text-ink-2">Dismissed: {g.dismissReason}</div>}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {isOpen ? (
          <>
            {(g.suggestedResolutions || []).slice(0, 3).map((a) => <Button key={a} size="sm" variant="outline" onClick={() => onResolve(g, a)} data-testid={`pg-gap-action-${g.id}-${a}`}>{ACTION_LABEL[a] || humanize(a)}</Button>)}
            <Button size="sm" onClick={() => onResolve(g, null)} data-testid={`pg-gap-resolve-${g.id}`}>Resolve Gap</Button>
            <Button size="sm" variant="ghost" onClick={() => onDismiss(g)} data-testid={`pg-gap-dismiss-${g.id}`}>Dismiss</Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={() => onReopen(g)} data-testid={`pg-gap-reopen-${g.id}`}><ExternalLink className="mr-1 h-3.5 w-3.5" /> Reopen</Button>
        )}
      </div>
    </div>
  );
}
